import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calculator as CalcIcon, DollarSign, Percent, ArrowDown, ArrowUp, Target, Zap, AlertTriangle, TrendingUp, TrendingDown, Save, Loader2, ChevronDown, Star, XCircle, FileText, Scale, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import EstimatedFeesField from '../components/EstimatedFeesField';
import BrokerFeesBanner from '../components/BrokerFeesBanner';
import { useEstimatedFees } from '../hooks/useEstimatedFees';
import OnboardingTutorial from '../components/OnboardingTutorial';
import { CALCULATOR_SLIDES } from '../config/tutorialSlides';
import TradingViewLink from '../components/TradingViewLink';
import HowItWorks from '../components/HowItWorks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LocalStorage key for guest watchlist (same as Watchlist.js)
const LOCAL_WATCHLIST_KEY = 'tradex_guest_watchlist';

const Calculator = () => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { fees: estimatedFeesPct } = useEstimatedFees();
  const [saving, setSaving] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [showSymbolDropdown, setShowSymbolDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    symbol: 'BTC/USDT',
    capital: 10000,
    riskPercent: 1,
    leverage: 10,
    entryPrice: 0,
    stopLoss: 0,
    takeProfit: 0,
  });

  // Get watchlist from localStorage for guests
  const getLocalWatchlist = () => {
    try {
      const saved = localStorage.getItem(LOCAL_WATCHLIST_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse local watchlist:', e);
    }
    // Default watchlist
    return [
      { symbol: 'BINANCE:BTCUSDT', name: 'Bitcoin', category: 'crypto' },
      { symbol: 'FX:EURUSD', name: 'EUR/USD', category: 'forex' },
      { symbol: 'NASDAQ:AAPL', name: 'Apple Inc.', category: 'stocks' }
    ];
  };

  // Fetch watchlist for symbol selection
  const fetchWatchlist = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const response = await axios.get(`${API}/watchlist/symbols`);
        setWatchlist(response.data.symbols || []);
      } catch (error) {
        console.error('Failed to fetch watchlist:', error);
        setWatchlist(getLocalWatchlist());
      }
    } else {
      // Guest: load from localStorage
      setWatchlist(getLocalWatchlist());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWatchlist();
  }, [fetchWatchlist]);

  // Determine direction based on entry and stop loss
  const isLong = formData.entryPrice > formData.stopLoss && formData.stopLoss > 0;
  const isShort = formData.entryPrice < formData.stopLoss && formData.entryPrice > 0;
  const direction = isLong ? 'LONG' : isShort ? 'SHORT' : null;

  // Calculate all values - PERTE MAX AU SL FIXÉE
  const result = useMemo(() => {
    const { capital, riskPercent, leverage, entryPrice, stopLoss, takeProfit } = formData;
    
    if (!entryPrice || !stopLoss || entryPrice === stopLoss) {
      return null;
    }

    // =====================================================
    // LOGIQUE: La PERTE AU SL est fixée (pas la marge)
    // Plus le SL est serré, plus la position est grande
    // =====================================================
    
    // Perte max au SL = Capital × %Risque
    const perteMaxSL = capital * (riskPercent / 100);
    
    let risqueParUnite, gainParUniteTp, niveauLiquidation;
    
    if (isLong) {
      risqueParUnite = Math.abs(entryPrice - stopLoss);
      gainParUniteTp = takeProfit > 0 ? takeProfit - entryPrice : 0;
      niveauLiquidation = leverage > 1 ? entryPrice * (1 - 1/leverage) : 0;
    } else {
      risqueParUnite = Math.abs(stopLoss - entryPrice);
      gainParUniteTp = takeProfit > 0 ? entryPrice - takeProfit : 0;
      niveauLiquidation = leverage > 1 ? entryPrice * (1 + 1/leverage) : 0;
    }
    
    if (risqueParUnite === 0) risqueParUnite = 0.0001;
    
    // Quantité = Perte max / Distance SL
    const quantiteCoin = perteMaxSL / risqueParUnite;
    
    // Taille position = Quantité × Entry
    const taillePosition = quantiteCoin * entryPrice;
    
    // Marge requise = Taille position / Levier
    const marge = leverage > 1 ? taillePosition / leverage : taillePosition;
    
    // Perte au SL = perteMaxSL (par définition)
    const perteSL = perteMaxSL;
    
    // Gain au TP
    const gainTotal = takeProfit > 0 ? quantiteCoin * gainParUniteTp : 0;
    
    // ROI basé sur la marge
    const roiPerte = marge > 0 ? (perteSL / marge) * 100 : 0;
    const roiGain = marge > 0 && gainTotal > 0 ? (gainTotal / marge) * 100 : 0;
    
    // Ratio R:R = Gain / Perte au SL
    const ratioRR = perteSL > 0 && gainTotal > 0 ? gainTotal / perteSL : 0;
    
    // Vérifier si SL est avant liquidation
    const slBeforeLiquidation = isLong 
      ? stopLoss > niveauLiquidation 
      : stopLoss < niveauLiquidation;
    
    // Perte si liquidé = Marge (toute la marge)
    const perteLiquidation = marge;
    
    // Verdict
    const ratioFormatted = Math.round(ratioRR * 100) / 100;
    let verdict = '';
    if (!slBeforeLiquidation && leverage > 1) {
      verdict = `⚠️ SL après liquidation - Vous serez liquidé avant !`;
    } else if (ratioRR >= 2) {
      verdict = `✓ R:R ${ratioFormatted}:1 - Trade valide`;
    } else if (ratioRR > 0) {
      verdict = `✗ R:R ${ratioFormatted}:1 - Ratio insuffisant (min 2:1)`;
    }
    
    return {
      perteMaxSL: Math.round(perteMaxSL * 100) / 100,
      marge: Math.round(marge * 100) / 100,
      taillePosition: Math.round(taillePosition * 100) / 100,
      quantiteCoin: quantiteCoin,
      niveauLiquidation: Math.round(niveauLiquidation * 100) / 100,
      risqueParUnite: Math.round(risqueParUnite * 100) / 100,
      perteSL: Math.round(perteSL * 100) / 100,
      perteLiquidation: Math.round(perteLiquidation * 100) / 100,
      gainParUnite: Math.round(gainParUniteTp * 100) / 100,
      gainTotal: Math.round(gainTotal * 100) / 100,
      roiPerte: Math.round(roiPerte * 100) / 100,
      roiGain: Math.round(roiGain * 100) / 100,
      ratioRR: Math.round(ratioRR * 100) / 100,
      verdict: verdict,
      slBeforeLiquidation: slBeforeLiquidation,
    };
  }, [formData, isLong]);

  const leverageWarning = formData.leverage > 50;

  // Save trade to Action
  const saveToAction = async (isPaper = false) => {
    if (!result) {
      toast.error(language === 'fr' ? 'Calculez d\'abord la position' : 'Calculate position first');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/trades`, {
        symbol: formData.symbol,
        direction: direction,
        trade_type: 'SWING',
        entry_price: formData.entryPrice,
        stop_loss: formData.stopLoss,
        take_profit_100: formData.takeProfit || null,
        leverage: formData.leverage,
        position_size: result.marge,  // Marge en $, pas quantité de coins
        status: 'pending',
        is_paper: isPaper,
        notes: `Capital: $${formData.capital}, Risque: ${formData.riskPercent}%`,
      });
      toast.success(
        language === 'fr' 
          ? `Trade ${isPaper ? 'Paper' : 'Real'} sauvegardé dans Action !` 
          : `${isPaper ? 'Paper' : 'Real'} trade saved to Action!`
      );
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] p-4 sm:p-6 lg:p-8">
      {/* Onboarding Tutorial */}
      <OnboardingTutorial
        pageId="calculator"
        slides={CALCULATOR_SLIDES}
        storageKey="tutorial_calculator_seen"
      />

      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">{language === 'fr' ? 'Calculateur de Position' : 'Position Calculator'}</h1>
            <HowItWorks pageKey="calculator" />
            <p className="text-muted-foreground mt-1">{language === 'fr' ? 'Calcul de taille de position basé sur le risque (formules Futures)' : 'Risk-based position sizing (Futures formulas)'}</p>
          </div>
          <TradingViewLink symbol={formData.symbol} label="TradingView" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="bg-card border-white/10" data-testid="calculator-form">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalcIcon className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Paramètres' : 'Parameters'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Symbol with Watchlist Dropdown */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-muted-foreground" />
                  {language === 'fr' ? 'Symbole' : 'Symbol'}
                </Label>
                <div className="relative">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        type="text"
                        className="bg-secondary/50 border-white/10 font-mono text-lg pr-10"
                        value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
                        onFocus={() => setShowSymbolDropdown(true)}
                        placeholder="BTC/USDT"
                        data-testid="calc-symbol-input"
                      />
                      <button 
                        type="button"
                        onClick={() => setShowSymbolDropdown(!showSymbolDropdown)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded"
                      >
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showSymbolDropdown ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Dropdown for watchlist symbols */}
                  {showSymbolDropdown && watchlist.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-white/10 flex items-center gap-2 text-xs text-muted-foreground">
                        <Star className="w-3 h-3 text-yellow-400" />
                        {language === 'fr' ? 'Mes Favoris' : 'My Favorites'}
                      </div>
                      {watchlist.map((item, index) => (
                        <button
                          key={item.symbol || index}
                          type="button"
                          onClick={() => {
                            // Convert TradingView format to simple format for display
                            let displaySymbol = item.symbol;
                            if (item.symbol.includes(':')) {
                              const ticker = item.symbol.split(':')[1];
                              // Handle crypto pairs (BTCUSDT -> BTC/USDT)
                              if (ticker.endsWith('USDT')) {
                                displaySymbol = ticker.replace('USDT', '/USDT');
                              } else if (ticker.endsWith('USD') && !ticker.endsWith('USDT')) {
                                displaySymbol = ticker.replace('USD', '/USD');
                              } else {
                                displaySymbol = ticker;
                              }
                            }
                            setFormData({ ...formData, symbol: displaySymbol });
                            setShowSymbolDropdown(false);
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-white/5 flex items-center justify-between gap-2 transition-colors"
                          data-testid={`watchlist-option-${index}`}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{item.name}</span>
                            <span className="text-xs text-muted-foreground font-mono">{item.symbol}</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            item.category === 'crypto' ? 'bg-orange-500/20 text-orange-400' :
                            item.category === 'stocks' ? 'bg-green-500/20 text-green-400' :
                            item.category === 'forex' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {item.category}
                          </span>
                        </button>
                      ))}
                      <div className="p-2 border-t border-white/10 text-xs text-muted-foreground text-center">
                        {language === 'fr' ? 'Ou saisissez un symbole manuellement' : 'Or type a symbol manually'}
                      </div>
                    </div>
                  )}
                </div>
                {/* Click outside to close */}
                {showSymbolDropdown && (
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowSymbolDropdown(false)}
                  />
                )}
              </div>

              {/* Capital */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  {language === 'fr' ? 'Capital (Marge initiale)' : 'Capital (Initial Margin)'}
                </Label>
                <Input
                  type="number"
                  data-testid="calc-capital-input"
                  className="bg-secondary/50 border-white/10 font-mono text-lg"
                  value={formData.capital}
                  onChange={(e) => setFormData({ ...formData, capital: parseFloat(e.target.value) || 0 })}
                />
              </div>

              {/* Risk % */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Percent className="w-4 h-4 text-muted-foreground" />
                  {language === 'fr' ? '% Risque par trade' : 'Risk % per trade'}
                </Label>
                <Input
                  type="number"
                  data-testid="calc-risk-input"
                  step="0.5"
                  min="0.5"
                  max="10"
                  className="bg-secondary/50 border-white/10 font-mono text-lg"
                  value={formData.riskPercent}
                  onChange={(e) => setFormData({ ...formData, riskPercent: parseFloat(e.target.value) || 1 })}
                />
              </div>

              {/* Leverage */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  {language === 'fr' ? 'Levier' : 'Leverage'}
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    step="1"
                    className="bg-secondary/50 border-white/10 font-mono text-lg pr-8"
                    value={formData.leverage}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setFormData({ ...formData, leverage: Math.max(1, Math.min(200, val)) });
                    }}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">x</span>
                </div>
                {formData.leverage > 50 && (
                  <p className="text-xs text-yellow-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {language === 'fr' ? 'Levier élevé = risque élevé' : 'High leverage = high risk'}
                  </p>
                )}
              </div>

              {/* Entry Price */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-muted-foreground" />
                  {language === 'fr' ? "Prix d'entrée" : 'Entry Price'}
                </Label>
                <Input
                  type="number"
                  data-testid="calc-entry-input"
                  step="0.01"
                  className="bg-secondary/50 border-white/10 font-mono text-lg"
                  value={formData.entryPrice || ''}
                  onChange={(e) => setFormData({ ...formData, entryPrice: parseFloat(e.target.value) || 0 })}
                />
              </div>

              {/* Stop Loss */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ArrowDown className="w-4 h-4 text-destructive" />
                  Stop-Loss
                </Label>
                <Input
                  type="number"
                  data-testid="calc-sl-input"
                  step="0.01"
                  className="bg-secondary/50 border-white/10 font-mono text-lg"
                  value={formData.stopLoss || ''}
                  onChange={(e) => setFormData({ ...formData, stopLoss: parseFloat(e.target.value) || 0 })}
                />
              </div>

              {/* Take Profit */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-primary" />
                  Take-Profit
                </Label>
                <Input
                  type="number"
                  data-testid="calc-tp-input"
                  step="0.01"
                  className="bg-secondary/50 border-white/10 font-mono text-lg"
                  value={formData.takeProfit || ''}
                  onChange={(e) => setFormData({ ...formData, takeProfit: parseFloat(e.target.value) || 0 })}
                />
              </div>

              {/* Direction Indicator */}
              {direction && (
                <div className={`p-3 rounded-lg text-center font-mono font-bold flex items-center justify-center gap-2 ${
                  isLong ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'
                }`}>
                  {isLong ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  {direction}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          <div className={!isAuthenticated ? 'relative' : ''} data-testid="calculator-results-wrapper">
          <Card className={`bg-card border-white/10 transition-opacity ${result ? 'opacity-100' : 'opacity-50'} ${!isAuthenticated ? 'blur-md pointer-events-none select-none' : ''}`} data-testid="calculator-result" aria-hidden={!isAuthenticated}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                {language === 'fr' ? 'Résultats' : 'Results'}
                {result?.verdict && (
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-mono ${
                    result.verdict.includes('Valide') ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'
                  }`}>
                    {result.verdict}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {result ? (
                <>
                  {/* Position Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                      <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Perte Max au SL' : 'Max Loss at SL'}</div>
                      <div className="text-xl font-mono font-bold text-destructive">-${result.perteSL.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">= {language === 'fr' ? 'Risque fixé' : 'Fixed risk'}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Taille Position' : 'Position Size'}</div>
                      <div className="text-xl font-mono font-bold text-yellow-400">${result.taillePosition.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Marge' : 'Margin'}: ${result.marge.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="p-3 rounded-lg bg-secondary/50">
                    <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Quantité COIN' : 'COIN Quantity'}</div>
                    <div className="text-2xl font-mono font-bold text-primary">{result.quantiteCoin.toFixed(6)}</div>
                  </div>

                  {/* Gain */}
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Gain au TP' : 'Gain at TP'}</div>
                      <EstimatedFeesField compact showLabel={false} showModalTrigger={false} />
                    </div>
                    <div className="text-xl font-mono font-bold text-primary">+${result.gainTotal.toLocaleString()}</div>
                    <div className="text-xs text-primary/70">ROI: +{result.roiGain.toFixed(1)}%</div>
                    <div className="mt-1 text-xs text-muted-foreground/80 font-mono" data-testid="calc-net-gain">
                      {language === 'fr' ? 'Net après frais' : 'Net after fees'} ({estimatedFeesPct.toFixed(2)}%):
                      <span className="ml-1 text-primary/90">
                        +${Math.max(0, result.gainTotal - (result.taillePosition * estimatedFeesPct / 100)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  {/* Per Unit */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-secondary/30">
                      <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Distance SL' : 'SL Distance'}</div>
                      <div className="font-mono font-bold">${result.risqueParUnite.toLocaleString()}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-secondary/30">
                      <div className="text-xs text-muted-foreground mb-1">{language === 'fr' ? 'Distance TP' : 'TP Distance'}</div>
                      <div className="font-mono font-bold">${result.gainParUnite.toLocaleString()}</div>
                    </div>
                  </div>

                  {/* R:R Ratio */}
                  <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="text-xs text-muted-foreground mb-1">Ratio R:R</div>
                    <div className="text-2xl font-mono font-bold text-accent">{result.ratioRR}:1</div>
                  </div>

                  {/* Liquidation */}
                  {formData.leverage > 1 && (
                    <div className={`p-3 rounded-lg border ${result.slBeforeLiquidation ? 'bg-red-500/10 border-red-500/20' : 'bg-red-500/30 border-red-500/50'}`}>
                      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-red-400" />
                        {language === 'fr' ? 'Niveau de Liquidation' : 'Liquidation Level'}
                        {!result.slBeforeLiquidation && (
                          <span className="ml-2 px-1 py-0.5 bg-red-500 text-white text-xs rounded">
                            ⚠️ SL inefficace !
                          </span>
                        )}
                      </div>
                      <div className="text-xl font-mono font-bold text-red-400">${result.niveauLiquidation.toLocaleString()}</div>
                      <div className="text-xs text-red-400/70">
                        {language === 'fr' ? 'Perte si liquidé' : 'Loss if liquidated'}: -${result.perteLiquidation.toLocaleString()} ({language === 'fr' ? 'toute la marge' : 'full margin'})
                      </div>
                    </div>
                  )}

                  {/* Formulas */}
                  <details className="p-3 rounded-lg bg-secondary/20">
                    <summary className="text-sm font-medium text-muted-foreground cursor-pointer">
                      {language === 'fr' ? 'Voir les formules' : 'View formulas'}
                    </summary>
                    <div className="mt-3 font-mono text-xs space-y-1 text-muted-foreground">
                      <div className="text-yellow-400 pb-2 border-b border-white/10">
                        📌 Perte fixée au SL = Capital × %Risque = ${formData.capital} × {formData.riskPercent}% = ${result.perteSL}
                      </div>
                      <div>Distance SL = |Entry - SL| = |${formData.entryPrice} - ${formData.stopLoss}| = ${result.risqueParUnite}</div>
                      <div>Quantité = Perte SL / Distance SL = ${result.perteSL} / ${result.risqueParUnite} = {result.quantiteCoin.toFixed(6)}</div>
                      <div>Position = Quantité × Entry = {result.quantiteCoin.toFixed(6)} × ${formData.entryPrice} = ${result.taillePosition}</div>
                      <div>Marge = Position / Levier = ${result.taillePosition} / {formData.leverage} = ${result.marge}</div>
                      <div>Gain TP = Quantité × Distance TP = {result.quantiteCoin.toFixed(6)} × ${result.gainParUnite} = ${result.gainTotal}</div>
                      <div className="text-green-400 pt-2 border-t border-white/10">
                        ✓ Plus le SL est serré → Plus la quantité est grande → Plus les gains sont élevés
                      </div>
                    </div>
                  </details>

                  {/* Warning if R:R < 2:1 */}
                  {result.ratioRR > 0 && result.ratioRR < 2 && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                      <div className="flex items-start gap-2">
                        <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-red-400">
                            {language === 'fr' ? 'Trade non recommandé' : 'Trade not recommended'}
                          </p>
                          <p className="text-xs text-red-400/80 mt-1">
                            {language === 'fr' 
                              ? `Le ratio risque/rendement de ${result.ratioRR}:1 est inférieur au minimum recommandé de 2:1. Vous pouvez quand même lancer ce trade, mais le risque est plus élevé.`
                              : `The risk/reward ratio of ${result.ratioRR}:1 is below the recommended minimum of 2:1. You can still launch this trade, but the risk is higher.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Save to Action Buttons - Real and Paper */}
                  <div className="space-y-3 mt-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={() => saveToAction(false)}
                        disabled={saving || !direction}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                        data-testid="save-real-trade-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        {language === 'fr' ? 'Trade Réel' : 'Real Trade'}
                      </Button>
                      <Button 
                        onClick={() => saveToAction(true)}
                        disabled={saving || !direction}
                        variant="outline"
                        className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10 font-bold"
                        data-testid="save-paper-trade-btn"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4 mr-2" />
                        )}
                        {language === 'fr' ? 'Trade Paper' : 'Paper Trade'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      {language === 'fr' 
                        ? 'Le trade sera ajouté au Journal avec le statut "En attente"' 
                        : 'Trade will be added to Journal with "Pending" status'}
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CalcIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{language === 'fr' ? 'Entrez les paramètres pour calculer' : 'Enter parameters to calculate'}</p>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Permanent broker fees reminder — click to open detailed modal */}
          <div className="mt-3" data-testid="calculator-broker-fees-wrapper">
            <BrokerFeesBanner />
          </div>
          {!isAuthenticated && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center p-4"
              data-testid="calculator-gate-overlay"
            >
              <Card className="max-w-md w-full bg-card/95 backdrop-blur-md border-primary/30 shadow-2xl">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    {language === 'fr' ? 'Résultats réservés aux membres' : 'Results for members only'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr'
                      ? 'Créez un compte gratuit pour voir votre taille de position, marge, R:R, niveau de liquidation et sauvegarder vos trades.'
                      : 'Create a free account to see your position size, margin, R:R, liquidation level and save your trades.'}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
                    <Link to="/register" className="flex-1">
                      <Button
                        className="w-full bg-primary hover:bg-primary/90 font-bold"
                        data-testid="calculator-gate-register-btn"
                      >
                        {language === 'fr' ? 'Créer un compte gratuit' : 'Create a free account'}
                      </Button>
                    </Link>
                    <Link to="/login" className="flex-1">
                      <Button
                        variant="outline"
                        className="w-full border-white/20"
                        data-testid="calculator-gate-login-btn"
                      >
                        {language === 'fr' ? 'Se connecter' : 'Log in'}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
